import { REVIEW } from '@sonder/shared'
import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router'
import { useMode } from '@/app/mode'
import { useAllSections, useBrandCount, useProgress } from '@/data/client'
import { useCallable } from '@/data/hooks'
import { Button, Eyebrow, Notice, ProgressStrip, useToast } from '@/ui'
import type { PageProps } from './ClientRoutes'
import type { WorkspaceContext } from './WorkspaceLayout'
import { ReviewAnswers } from './ReviewAnswers'
import { StepSummary } from './StepSummary'
import { reviewAnchor, useReviewRows } from './reviewRows'

export function ReviewPage({ clientId, client, config, base }: PageProps) {
  const { docs } = useAllSections(clientId)
  const brandCount = useBrandCount(clientId)
  const progress = useProgress(config, docs)
  const submit = useCallable<{ clientId: string }, { submissionId: string; missing: number; unconfirmed: number }>('submitOnboarding')
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()
  const { toast } = useToast()
  const mode = useMode()
  const { progress: headline } = useOutletContext<WorkspaceContext>()
  const rows = useReviewRows(config, docs, brandCount)
  const unconfirmedTotal = Object.values(progress?.sections ?? {}).reduce((a, s) => a + s.unconfirmed, 0)

  const onSubmit = async () => {
    setBusy(true)
    try {
      await submit({ clientId })
      nav(`${base}/submitted`)
    } catch (e) {
      toast((e as Error).message ?? 'Could not submit', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-10">
      <header className="grid max-w-form gap-3">
        <Eyebrow>Review</Eyebrow>
        <h1 className="text-h2">{REVIEW.title}</h1>
        <p className="text-charcoal">{REVIEW.body}</p>
      </header>
      {unconfirmedTotal > 0 && (
        <Notice tone="warn">
          {unconfirmedTotal} pre-filled {unconfirmedTotal === 1 ? 'field is' : 'fields are'} still waiting for a “Looks right”. You can submit anyway; we’ll confirm them together.
        </Notice>
      )}
      {/* The steps at a glance; each jumps to its answers below. */}
      <StepSummary config={config} brandCount={brandCount} progress={progress} onSelect={jumpTo} />
      <ReviewAnswers config={config} docs={docs} rows={rows} brandCount={brandCount} editHref={(id) => `${base}/onboarding/${id}`} team={mode === 'prep'} showEmptySteps />
      {/* Pinned to the bottom of the window like the steps' Next bar (progress included below lg), so Submit is never a long scroll away. */}
      <div className="sticky bottom-0 z-20 -mx-1 grid gap-2.5 border-t border-gray-200 bg-paper/95 px-1 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:pt-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
        <ProgressStrip {...headline} className="lg:hidden" />
        <div className="flex flex-wrap items-center gap-4">
          <Button onClick={() => void onSubmit()} disabled={busy || mode !== 'client'}>
            {busy ? 'Submitting…' : client.status === 'submitted' ? REVIEW.resubmit : REVIEW.submit}
          </Button>
          {mode === 'prep' && <span className="text-small text-dim">Submitting is the client’s step.</span>}
          {mode === 'preview' && <span className="text-small text-dim">Submitting is off while previewing.</span>}
        </div>
      </div>
    </div>
  )
}

function jumpTo(id: string) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  document.getElementById(reviewAnchor(id))?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
}
